// eslint-disable-next-line no-unused-vars
import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { object, bool, func, string, array, number } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionItem, Button, SkeletonText } from '@carbon/react';
import noop from 'lodash/noop';
import {
  UserRound,
  LayoutDashboard,
  ShoppingCart,
  Heart,
  Package,
  MapPinHouse,
  UsersRound,
  ChartNoAxesCombined,
  Combine,
  SquareCode,
  Info,
  Eye,
  FileText,
} from 'lucide-react';

import LoginForm from 'Components/Login/LoginForm';
import AccountManager from 'Containers/AccountManager/AccountManager';
import { Can } from 'Components/shared';
import { CURRENCY_EUR, CURRENCY_RON, PERMISSION } from 'Constants';
import useNumberUtils from 'Hooks/useNumberUtils';
import useUser from 'Hooks/useUser';
import ExchangeRatesSection from 'Components/SettingsBar/ExchangeRate';

const StyledContainer = styled.div`
  position: relative;

  .formContainer {
    flex-grow: 1;
    background-color: transparent;
    color: ${ props => props.theme.colors.darkGray };
    border-color: ${ props => props.theme.colors.darkGray };
    padding: 1.15rem 1.25rem;

    form {
      padding: 0;

      .submit {
        text-align: center;
        padding: 0;
        max-width: none;
        width: 100%;
        justify-content: center;
        font-size: 1rem;
      }

      > p {
        font-size: 0.875rem;
      }

      .loginHelpers {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 0.875rem;

        button {
          &:active, &:focus {
            font-weight: 600;
          }

          outline: 0 none;
          border: 0 none;
          padding: 0;
          box-shadow: none;
          background: transparent;
        }
      }
    }

    > div {
      background-color: ${ props => props.theme.colors.lightBlue };
      padding: 15px 25px;
      display: flex;
      justify-content: center;

      button {
        color: #223F64;
        background-color: ${ props => props.theme.colors.white };
        width: 100%;
        max-width: none;
        border: 1px solid #CAD3DE;
        justify-content: center;
        font-size: 1rem;
        padding: 0;
      }
    }
  }

  button.logout {
    justify-content: flex-start;
    padding: 0 1rem;
  }
`;

const StyledButton = styled(Button)`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  outline: 0;
  border: 0;
  box-shadow: none !important;
  background-color: transparent !important;
  color: ${ props => props.theme.colors.white };
  font-size: 1rem;

  &:before {
    display: none;
    position: absolute;
    top: 32px;
    width: 10px;
    height: 10px;
    background-color: #F3F2F2;
    transform: rotate(45deg);
    content: '';
    opacity: ${ props => props.theme.open ? 1 : 0 };
    visibility: ${ props => props.theme.open ? 'visible' : 'hidden' };
    transition: opacity 0.25s ease-out;
  }

  svg {
    &.caret {
      transform: rotate(${ props => props.theme.open ? '180deg' : '0' });
      transition: transform 0.2s ease;
    }
    margin: 0 5px;
  }

  @media screen and (max-width: ${ props => props.theme.device.tablet }) {
    width: 34px;

    &:before {
      top: 39px;
      display: block;
    }

    span {
      display: none;
    }
  }
  
  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    &:before {
      top: 32px;
    }
  }
`;

const DropdownMenu = styled.div`
  &:after {
    display: block;
    position: absolute;
    top: -5px;
    right: 1.5rem;
    width: 10px;
    height: 10px;
    background-color: ${ props => props.theme.colors.white };
    transform: rotate(45deg);
    content: '';
  }

  position: absolute;
  top: 50px;
  right: -1rem;
  width: 300px;
  color: ${ props => props.theme.colors.darkBlue };
  background-color: ${ props => props.theme.colors.white };
  z-index: 9;
  box-shadow: 0 3px 6px #00000029;
  opacity: ${ props => props.open ? 1 : 0 };
  visibility: ${ props => props.open ? 'visible' : 'hidden' };
  transition: opacity 0.25s ease-out;

  .userContainer {
    color: ${ props => props.theme.colors.darkGray };
    font-size: 0.875rem;
    max-height: calc(100vh - ${ props => props.sticky ? '70px' : '135px'});
    overflow-y: auto;

    .user {
      span {
        text-transform: capitalize;
        font-weight: 700;
      }
    }

    .menu {
      a, p {
        display: flex;
        align-items: center;
        padding: 0;
        span {
          width: 27px;
          margin-right: 8px;
        }
      }
    }

    .menu .innerMenu li {
      &:hover {
        background-color: ${ props => props.theme.colors.altGray };
      }

      .cds--accordion__content {
        display: none;
      }
    }

    .menu li {
      &.noDropdown {
        .cds--accordion__arrow {
          display: none;
        }
      }
      &:hover {
        background-color: ${ props => props.theme.colors.shadowGray };
      }

      .cds--accordion__wrapper {
        padding: 0;
      }

      button {
        &:hover,
        &:focus {
          &:before {
            display: none;
          }
          background-color: transparent;
        }
        height: 40px;
        min-height: 0;
        padding: 0;
        align-items: center;
      }

      .cds--accordion__content {
        padding: 0 20px 0 40px;
        width: 100%;

        ul li {
          padding: 0;
        }
      }

      .cds--accordion__title {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        margin: 0;

        span.icon {
          width: 24px;
          text-align: center;
          display: inline-block;
          margin-right: 8px;
        }
      }
      display: flex;
      align-items: flex-start;
      justify-content: flex-start;
      flex-direction: column;
      padding: 0 16px;
      border: 0;

    }
    p, a {
      font-size: inherit;
      color: inherit;
      padding: 0 15px;
      margin: 10px 0;
    }
    a {
      color: ${ props => props.theme.colors.darkGray } !important;
      width: 100%;
    }
    hr {
      width: 100%;
      height: 2px;
      border: 0;
      background-color: ${ props => props.theme.colors.shadowGray };
    }
    .form {
      p {
        margin: 0;
        padding: 10px 0;

        &.logoutReseller {
          background-color: #41618A;
          color: #fff;

          &:hover {
            background-color: #41618A;
          }
        }

        &:hover {
          background-color: ${ props => props.theme.colors.shadowGray };
        }
      }
      button {
        color: inherit;
        height: 30px;
        width: 100%;
        background-color: transparent;
        box-shadow: none;
        outline: 0;
        min-height: 0;
      }
    }
  }

  .login {
    input,
    button {
      margin: 0.5rem 0;
      width: 100%;
    }

    .no-width {
      justify-content: space-between;
    }

    .no-width input,
    .no-width button {
      width: auto;
      margin: 0;
    }
  }

  .partnerShip {
    background-color: ${ props => props.theme.colors.lightBlue };
    padding: 15px 20px;
    margin-top: 5px;
    display: flex;
    justify-content: center;

    button {
      color: #223F64;
      background-color: ${ props => props.theme.colors.white };
      width: 100%;
      height: 40px;
      max-width: none;
      border: 1px solid #CAD3DE;
      justify-content: center;
      font-size: 1rem;
      padding: 0;
    }
  }

  hr {
    background-color: ${ props => props.theme.products.colors.border };
    margin: 0;
  }

  .mobileContainer, .accountInfo {
    display: none;
  }

  @media screen and (max-width: ${ props => props.theme.device.tablet }) {
    position: fixed;
    width: auto;
    inset: ${ props => props.sticky ? '103px' : '115px' } 0 0 0;
    height: calc(100vh - 94px);
    overflow-y: scroll;

    &:after {
      right: 210px;
      background: #F3F2F2;
    }

    .userContainer {
      max-height: none;
      
      .user {
        background: #F3F2F2;
        margin: 0;
        padding: 20px 15px 10px;
      }

      p, a {
        padding: 0;
      }
    }

    .mobileContainer {
      display: block;
      background: #F3F2F2;
      padding-bottom: 18px;

      .summary {
        display: flex;
        padding: 0 15px;
        gap: 15px;

        > div {
          width: 50%;
        }

        .details {
          p {
            margin: 0;
            padding: 0;

            &.phone a {
              color: #D52B1E !important;
            }

            &.title {
              padding: 0;
            }
          }
        }

        .resellerName {
          display: flex;
          justify-content: flex-start;
          align-items: flex-start;

          svg {
            margin-top: 6px;
          }

          .resellerData {
            display: flex;
            flex-direction: column;
            margin-left: 12px;
          }

          p {
            margin: 0;
            padding: 0;

            &.bold {
              font-weight: 600;
            }
          }
        }
      }
    }

    .accountInfo {
      display: flex;
      flex-direction: column;
      gap: 12px;
      background: #F3F2F2;
      border-top: 1px solid #E2E2E2;
      padding: 15px;
      font-size: 14px;

      .soldAndDue, .credit, .creditTotal {
        display: flex;

        div {
          display: flex;
          flex-direction: column;
          flex: 1;
          width: 50%;
          line-height: 20px;

          .value {
            font-weight: 600;

            &.soldValue {
              color: #E8720B;
            }

            &.creditLeftLabel {
              color: #498246;
            }
          }
        }
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    inset: 94px 0 0 0;
  }
`;

const StyledIndicator = styled.div`
  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    height: 12px;
    width: 100%;
    max-width: 0;
    ${ props => `max-width: ${ props.percentage() }%` };
    transition: max-width 0.35s ease;
    background-color: ${ props => props.theme.products.colors.isResealed };
    border-radius: 4px;
  }

  overflow: hidden;
  position: relative;
  height: 12px;
  border-radius: 5px;
  margin-top: 5px;
  background-color: #C3C5C8;
`;

const UserMenu = forwardRef((props, ref) => {
  const {
    user = {},
    onLogout,
    open,
    onToggle,
    isExternalReseller,
    defaultCurrency,
    credit,
    creditLoading,
    sold,
    soldLoading,
    dueDays,
    canFloat,
    isLoading,
  } = props;

  const theme = useTheme();
  const { t } = useTranslation();
  const { formatNumber } = useNumberUtils();
  const { data: { isImpersonated } = {} } = useUser();

  let creditRemaining;
  let creditTotal;

  if (isExternalReseller) {
    if (defaultCurrency === CURRENCY_EUR) {
      creditRemaining = credit?.remainingEur;
      creditTotal = credit?.totalEur;
    } else {
      creditRemaining = credit?.remainingUsd;
      creditTotal = credit?.totalUsd;
    }
  } else {
    creditRemaining = credit?.remaining;
    creditTotal = credit?.total;
  }

  return (
    <StyledContainer theme={ theme } ref={ ref }>
      <StyledButton theme={{ ...theme, open }} onClick={ onToggle }>
        <UserRound size={ 28 } />
        <span>{ t('profile.myAccount') }</span>
      </StyledButton>
      <DropdownMenu className="dr" theme={ theme } open={ open } sticky={ canFloat }>
        { (user?.userId && user?.loggedIn)
          ? (
            <div className="userContainer">
              <p className="user">
                { t('profile.welcome') }, <span>{ user.lastName }</span> <span>{ user.firstName }</span>!
              </p>
              <div className="mobileContainer">
                <ExchangeRatesSection
                  exchangeRates={ user?.exchangeRates }
                  exchangeRatesLoading={ isLoading }
                  formatNumber={ formatNumber }
                  t={ t }
                />
                <div className="summary">
                  {user?.resellerName && (
                    <div className={ `resellerName ${ user?.isImpersonated ? 'isImpersonated ' : '' }` }>
                      <Eye />
                      <div className="resellerData">
                        <p>{ t('profile.accountName') }:</p>
                        <p className="bold">{ user.resellerName }</p>
                      </div>
                    </div>
                  )}
                  <AccountManager className="account" />
                </div>
              </div>
              <Accordion className="menu">
                <AccordionItem
                  className="noDropdown"
                  title={ (
                    <Link to="/">
                      <span className="icon"><LayoutDashboard /></span>
                      { t('navigation.home') }
                    </Link>
                  ) }
                />
                <AccordionItem
                  className="noDropdown"
                  title={ (
                    <Link to="/account/profile">
                      <span className="icon"><UserRound className="profile" /></span>
                      { t('navigation.profile') }
                    </Link>
                  ) }
                >
                </AccordionItem>
                <Can permission={ PERMISSION.ACCESS_ACCOUNT_FEATURES }>
                  <AccordionItem
                    className="noDropdown"
                    title={ (
                      <Link to="/orders">
                        <span className="icon"><ShoppingCart className="cart" /></span>
                        { t('navigation.orders') }
                      </Link>
                    ) }
                  />
                  { !isExternalReseller && (
                    <AccordionItem
                      className="noDropdown"
                      title={ (
                        <Link to="/orders/reserved-products">
                          <span className="icon"><Package /></span>
                          { t('users.menu.productsReservations') }
                        </Link>
                      ) }
                    />
                  ) }
                </Can>
                { !isImpersonated && (
                  <Can permission={ PERMISSION.ACCESS_ACCOUNT_FEATURES }>
                    <AccordionItem
                      className="noDropdown"
                      title={ (
                        <Link to="/offers">
                          <span className="icon"><FileText /></span>
                          { t('offers.myOffers') }
                        </Link>
                      ) }
                    />
                  </Can>
                ) }
                <AccordionItem
                  className="noDropdown"
                  title={ (
                    <Link to="/lists">
                      <span className="icon"><Heart /></span>
                      { t('navigation.lists') }
                    </Link>
                  ) }
                />
                <Can permission={ PERMISSION.MANAGE_ADDRESSES }>
                  <AccordionItem
                    className="noDropdown"
                    title={ (
                      <Link to="/addresses">
                        <span className="icon"><MapPinHouse /></span>
                        { t('navigation.addresses') }
                      </Link>
                    ) }
                  />
                </Can>
                <Can permission={ PERMISSION.MANAGE_USERS }>
                  <AccordionItem
                    className="noDropdown"
                    title={ (
                      <Link to="/users">
                        <span className="icon"><UsersRound /></span>
                        { t('navigation.users') }
                      </Link>
                    ) }
                  />
                </Can>
                <Can permission={ PERMISSION.ACCESS_ACCOUNT_FEATURES }>
                  <AccordionItem
                    className="noDropdown"
                    title={ (
                      <Link to="/reports/invoices">
                        <span className="icon"><ChartNoAxesCombined /></span>
                        { t('users.menu.reports') }
                      </Link>
                    ) }
                  />
                  <AccordionItem
                    className="noDropdown"
                    title={ (
                      <Link to="/service/rma">
                        <span className="icon"><Combine /></span>
                        { t('users.menu.service') }
                      </Link>
                    ) }
                  />
                  <AccordionItem
                    className="noDropdown"
                    title={ (
                      <Link to="/api-integrations/api-information">
                        <span className="icon"><SquareCode /></span>
                        { t('navigation.api') }
                      </Link>
                    ) }
                  />
                  <AccordionItem
                    className="noDropdown"
                    title={ (
                      <Link to="/account/info">
                        <span className="icon"><Info size={ 18 } /></span>
                        { t('navigation.info') }
                      </Link>
                    ) }
                  />
                </Can>
              </Accordion>
              <hr />
              <div className="form">
                <p>
                  <Button
                    onClick={ onLogout }
                    data-test="onLogout"
                    type="button"
                    kind="ghost"
                    className="logout"
                  >
                    { t('profile.logout') }
                  </Button>
                </p>
              </div>
              {user?.isImpersonated && (
                <div className="form">
                  <p className="logoutReseller">
                    <Button
                      onClick={ () => onLogout({ isImpersonated: true }) }
                      data-test="onLogout"
                      type="button"
                      kind="ghost"
                      className="logout"
                    >
                      { `${t('profile.logout')} - ${user?.resellerName}` }
                    </Button>
                  </p>
                </div>
              )}
            </div>
          )
          : (
            <>
              <LoginForm { ...props } sessionExpired={ false } />
              <div className="partnerShip">
                <Button kind="tertiary" onClick={ noop }>
                  { t('profile.becomePartner') }
                </Button>
              </div>
            </>
          )
        }
        <div className="accountInfo">
          <div className="soldAndDue">
            <div>
              <span className="label">{ t('profile.soldValue') }:</span>
              { (sold === undefined || soldLoading)
                ? (<SkeletonText />)
                : sold.map(element => {
                  // TODO: remove this when endpoint does not send it anymore
                  return !(isExternalReseller && element?.currency === CURRENCY_RON) && (
                    <span className="soldValue value" key={element?.currency}>
                      { formatNumber(element?.value, { currency: element?.currency, maximumFractionDigits: element?.value === 0 ? 0 : 2 }) }
                    </span>
                  );
                })
              }
            </div>
            <div>
              <span className="label">{ t('profile.paymentTermShort') }:</span>
              { (dueDays === null || dueDays === undefined)
                ? <SkeletonText />
                : <span className="value">{ dueDays }</span>
              }
            </div>
          </div>
          <div className="credit">
            {(!credit || creditLoading)
              ? <SkeletonText /> : (
                <>
                  <div>
                    <span className="label">{ t('user.creditLeft') }:</span>
                    <span className="creditLeftLabel value">{ formatNumber(creditRemaining, { currency: defaultCurrency }) }</span>
                  </div>
                  <div>
                    <span className="label">{ t('user.consumedCredit') }:</span>
                    <span className="value">{ formatNumber((creditTotal || 0) - (creditRemaining || 0), { currency: defaultCurrency }) }</span>
                  </div>
                </>
              )}
          </div>
          <StyledIndicator
            theme={ theme }
            percentage={ () => {
              if (credit) {
                return ((creditRemaining / creditTotal) * 100);
              }
              return 0;
            } }
          />
          <div className="creditTotal">
            <div>
              <span className="label">{ t('user.creditLimit') }:</span>
              { (!credit || creditLoading)
                ? <SkeletonText />
                : <span className="value">{ formatNumber(creditTotal, { currency: defaultCurrency }) }</span>
              }
            </div>
          </div>
        </div>
      </DropdownMenu>
    </StyledContainer>
  );
});

UserMenu.propTypes = {
  user: object,
  username: string,
  password: string,
  loading: bool,
  open: bool,
  setDropdown: func,
  defaultListId: string,
  success: bool,
  error: string,
  // onOpen: func,
  // onClose: func,
  onToggle: func,
  onRemember: func,
  onChange: func,
  onSubmit: func,
  onLogout: func,
  isExternalReseller: bool,
  defaultCurrency: string,
  credit: object,
  creditLoading: bool,
  sold: array,
  soldLoading: bool,
  dueDays: number,
  canFloat: bool,
  isLoading: bool,
};

UserMenu.displayName = 'UserMenuComponent';

export default UserMenu;
